import useStores from 'shared/hooks/use-stores';
import { dollarsToCents } from 'shared/helpers/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import { useCheckoutLineItems } from 'src/checkout/hooks/use-checkout-line-items';
import { GetUserIncentiveEligibilityDocument } from 'src/gql/graphql';
import { ValidIncentiveValue, isValidIncentiveValue } from '../shared/incentive-types';

export type UseCashBackIncentivesOptions = {
  dispensaryId?: string;
  enabled?: boolean;
  failClosed?: boolean;
  totalAmount?: number;
};

export type CashBackIncentiveData = {
  eligible: boolean;
  incentive?: {
    id: string;
    name: string;
    description?: string | null;
    amountCents: number;
    cohortName: string;
    active: boolean;
  } | null;
};

export function useCashBackIncentives(
  options?: UseCashBackIncentivesOptions
): {
  incentiveData: CashBackIncentiveData | null;
  loading: boolean;
  error: any;
  shouldShowBanner: boolean;
  cashBackIncentiveValue: ValidIncentiveValue | undefined;
} {
  const { Cart, User } = useStores();
  const flags = useFlags();
  const checkoutLineItems = useCheckoutLineItems();

  const cashBackRedemptionEnabled = flags['fintech.incentives.redemption.fe.rollout'] ?? false;
  const cartTotal = checkoutLineItems.find((lineItem) => lineItem.type === 'total')?.value;
  const requestEnabled = options?.enabled ?? true;
  const failClosed = options?.failClosed ?? false;
  const dispensaryId = options === undefined ? Cart.order.dispensary?.id : options.dispensaryId;
  const totalAmount = options === undefined ? cartTotal : options.totalAmount;
  const totalAmountCents =
    totalAmount !== undefined && Number.isFinite(totalAmount) && totalAmount > 0
      ? dollarsToCents(totalAmount, true)
      : undefined;

  const shouldSkip = !requestEnabled || !cashBackRedemptionEnabled || !User.exists || !User.id || !dispensaryId;

  const { data, loading, error } = useQuery(GetUserIncentiveEligibilityDocument, {
    skip: shouldSkip,
    variables: {
      externalId: User.id,
      dispensaryId,
      ...(totalAmountCents !== undefined && { totalAmountCents }),
    },
    fetchPolicy: failClosed ? 'network-only' : 'cache-first',
    errorPolicy: failClosed ? 'all' : 'ignore',
  });

  const hasUsableResponse = !shouldSkip && (!failClosed || (!loading && !error));
  const incentiveData = hasUsableResponse ? data?.getUserIncentiveEligibility ?? null : null;
  const amountCents = incentiveData?.incentive?.amountCents;
  const calculatedValue =
    amountCents !== undefined && (!failClosed || amountCents % 100 === 0) ? Math.round(amountCents / 100) : undefined;
  const cashBackIncentiveValue = isValidIncentiveValue(calculatedValue) ? calculatedValue : undefined;
  const hasActiveIncentive = !failClosed || incentiveData?.incentive?.active === true;

  const shouldShowBanner = Boolean(
    incentiveData?.eligible && hasActiveIncentive && cashBackIncentiveValue !== undefined
  );

  return {
    incentiveData,
    loading,
    error,
    shouldShowBanner,
    cashBackIncentiveValue,
  };
}
